import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/enhanced-button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import posthog from "@/lib/posthog";
import { useAuth } from "@/contexts/AuthContext";
import { signIn, signUp, AuthFlowError } from "@/services/firebaseAuth.service";
import {
  demoLoginSchema,
  demoSignUpSchema,
  type DemoLoginFormValues,
  type DemoSignUpFormValues,
} from "@/schemas/demoAuth.schema";
import { USER_ROLES, USER_ROLE_LABELS } from "@/config/demoAuth.config";

const REDIRECT_TARGET = "/home";

const PasswordInput = ({
  value,
  onChange,
  onBlur,
  name,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  name: string;
  placeholder: string;
}) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <Input
        type={visible ? "text" : "password"}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        className="pr-10"
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
        aria-label={visible ? "Hide password" : "Show password"}
        tabIndex={-1}
      >
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
};

const Auth = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();

  const [tab, setTab] = useState<"login" | "signup">(
    location.pathname === "/signup" ? "signup" : "login",
  );
  const [isLoginSubmitting, setIsLoginSubmitting] = useState(false);
  const [isSignUpSubmitting, setIsSignUpSubmitting] = useState(false);

  useEffect(() => {
    if (!authLoading && user) {
      navigate(REDIRECT_TARGET, { replace: true });
    }
  }, [authLoading, user, navigate]);

  const loginForm = useForm<DemoLoginFormValues>({
    resolver: zodResolver(demoLoginSchema),
    defaultValues: { email: "", password: "" },
  });

  const signUpForm = useForm<DemoSignUpFormValues>({
    resolver: zodResolver(demoSignUpSchema),
    defaultValues: { email: "", password: "", role: "investor", organization: "", accessCode: "" },
  });

  const signUpRole = signUpForm.watch("role");
  const organizationLabel = signUpRole === "cfo" ? "Company" : "VC Firm Name";

  const handleTabChange = (value: string) => {
    const next = value === "signup" ? "signup" : "login";
    setTab(next);
    navigate(next === "signup" ? "/signup" : "/login", { replace: true });
  };

  const onLoginSubmit = async (values: DemoLoginFormValues) => {
    setIsLoginSubmitting(true);
    posthog.capture("login_started");
    try {
      await signIn(values.email, values.password);
      posthog.capture("user_logged_in");
      toast({ title: "Welcome back", description: "You're signed in." });
      navigate(REDIRECT_TARGET, { replace: true });
    } catch (error) {
      const message = error instanceof AuthFlowError ? error.message : "Unable to sign in. Please try again.";
      posthog.capture("login_failed", { reason: message });
      toast({ title: "Sign in failed", description: message, variant: "destructive" });
    } finally {
      setIsLoginSubmitting(false);
    }
  };

  const onSignUpSubmit = async (values: DemoSignUpFormValues) => {
    setIsSignUpSubmitting(true);
    posthog.capture("signup_started", { role: values.role });
    try {
      await signUp(values);
      posthog.capture("signup_completed", { role: values.role, organization: values.organization });
      toast({ title: "Account created", description: "Welcome to BlinkBooks." });
      navigate(REDIRECT_TARGET, { replace: true });
    } catch (error) {
      const message = error instanceof AuthFlowError ? error.message : "Unable to create account. Please try again.";
      posthog.capture("signup_failed", { role: values.role, reason: message });
      toast({ title: "Sign up failed", description: message, variant: "destructive" });
    } finally {
      setIsSignUpSubmitting(false);
    }
  };

  if (authLoading || user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="border-0 shadow-xl bg-card/95 backdrop-blur-sm">
          <CardContent className="p-8">
            <div className="mb-6 text-center">
              <div className="w-20 h-20 mx-auto mb-4">
                <img src="/content/logo.png" alt="BlinkBooks Logo" className="w-full h-full object-contain" />
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2">Welcome to BlinkBooks</h1>
              <p className="text-muted-foreground">Sign in or create a demo account</p>
            </div>

            <Tabs value={tab} onValueChange={handleTabChange}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login">Sign In</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <Form {...loginForm}>
                  <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="space-y-4">
                    <FormField
                      control={loginForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder="you@company.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={loginForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Password</FormLabel>
                          <FormControl>
                            <PasswordInput
                              name={field.name}
                              value={field.value}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              placeholder="Enter your password"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button type="submit" size="lg" className="w-full" disabled={isLoginSubmitting}>
                      {isLoginSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Signing in...
                        </>
                      ) : (
                        "Sign in"
                      )}
                    </Button>
                  </form>
                </Form>
              </TabsContent>

              <TabsContent value="signup">
                <Form {...signUpForm}>
                  <form onSubmit={signUpForm.handleSubmit(onSignUpSubmit)} className="space-y-4">
                    <FormField
                      control={signUpForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder="you@company.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={signUpForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Password</FormLabel>
                          <FormControl>
                            <PasswordInput
                              name={field.name}
                              value={field.value}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              placeholder="At least 6 characters"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={signUpForm.control}
                      name="role"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Role</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select your role" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {USER_ROLES.map((role) => (
                                <SelectItem key={role} value={role}>
                                  {USER_ROLE_LABELS[role]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={signUpForm.control}
                      name="organization"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{organizationLabel}</FormLabel>
                          <FormControl>
                            <Input placeholder={`Enter your ${organizationLabel.toLowerCase()}`} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={signUpForm.control}
                      name="accessCode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Access Code</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter your access code" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button type="submit" size="lg" className="w-full" disabled={isSignUpSubmitting}>
                      {isSignUpSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Creating account...
                        </>
                      ) : (
                        "Create account"
                      )}
                    </Button>
                  </form>
                </Form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Auth;
